import { useState, useEffect, useRef, useCallback } from "react";
import useChatStore from "../../store/chatStore";
import useAuthStore from "../../store/authStore";
import { useSocket } from "../../context/SocketContext";
import { getInitials, getConversationName } from "../../utils/helpers";
import styles from "./OnlineNotification.module.css";

const OnlineNotification = () => {
  const { onlineUsers, conversations } = useChatStore();
  const { user }                       = useAuthStore();
  const socketRef                      = useSocket();
  const [toasts, setToasts] = useState([]);
  const timerRefs           = useRef({});
  const prevOnlineRef       = useRef(null);
  const readyRef            = useRef(false);

  const addToast = useCallback((toast) => {
    const id = toast.id || (Date.now() + Math.random());
    setToasts((prev) => [...prev.slice(-4), { ...toast, id }]);
    timerRefs.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
      delete timerRefs.current[id];
    }, 4000);
  }, []);

  const removeToast = (id) => {
    clearTimeout(timerRefs.current[id]);
    delete timerRefs.current[id];
    setToasts((prev) => prev.filter((x) => x.id !== id));
  };

  // Online / Offline toasts
  useEffect(() => {
    if (prevOnlineRef.current === null) {
      prevOnlineRef.current = [...onlineUsers];
      if (conversations.length > 0) readyRef.current = true;
      return;
    }
    if (!readyRef.current) {
      prevOnlineRef.current = [...onlineUsers];
      if (conversations.length > 0) readyRef.current = true;
      return;
    }
    const prev   = prevOnlineRef.current;
    const people = {};
    conversations.forEach((c) =>
      c.participants?.forEach((p) => { if (p?._id && p?.name) people[p._id] = p; })
    );
    onlineUsers.forEach((id) => {
      if (id !== user?._id && !prev.includes(id) && people[id])
        addToast({ type: "online", name: people[id].name, avatar: people[id].avatar });
    });
    prev.forEach((id) => {
      if (id !== user?._id && !onlineUsers.includes(id) && people[id])
        addToast({ type: "offline", name: people[id].name, avatar: people[id].avatar });
    });
    prevOnlineRef.current = [...onlineUsers];
  }, [onlineUsers]);

  useEffect(() => {
    if (conversations.length > 0 && !readyRef.current) {
      readyRef.current      = true;
      prevOnlineRef.current = [...onlineUsers];
    }
  }, [conversations.length]);

  // New message toasts
  useEffect(() => {
    const unsub = useChatStore.subscribe(
      (state) => state._lastReceivedMessage,
      (newMsg, oldMsg) => {
        if (!newMsg || newMsg === oldMsg) return;
        if (newMsg.sender?._id === user?._id) return;
        const state    = useChatStore.getState();
        const convId   = newMsg.conversationId;
        const isMuted  = (state.mutedConversations || []).includes(convId);
        if (isMuted) return;
        if (state.activeConversation?._id === convId) return;
        const conv     = state.conversations.find((c) => c._id === convId);
        const convName = conv ? getConversationName(conv, user?._id) : (newMsg.sender?.name || "Someone");
        const body     = newMsg.isVoice ? "🎤 Voice message"
          : newMsg.image               ? "📷 Photo"
          : newMsg.gifUrl              ? "🎬 GIF"
          : newMsg.file                ? ("📎 " + (newMsg.fileName || "File"))
          : newMsg.text                || "New message";
        addToast({ type: "message", name: convName, subtext: body, avatar: newMsg.sender?.avatar, convId });
      }
    );
    return unsub;
  }, [user?._id]);

  // Status reaction toasts
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;
    const onReaction = ({ statusOwnerId, reactor, emoji, statusPreview }) => {
      if (statusOwnerId !== user?._id) return;
      addToast({
        type:          "statusReaction",
        name:          reactor.name,
        avatar:        reactor.avatar,
        emoji,
        statusPreview, // { mediaType, media, bgColor, textContent }
      });
    };
    socket.on("status:reaction", onReaction);
    return () => socket.off("status:reaction", onReaction);
  }, [socketRef?.current, user?._id]);

  if (toasts.length === 0) return null;

  return (
    <div className={styles.container}>
      {toasts.map((t) => (
        <div
          key={t.id}
          className={styles.toast + " " + (styles[t.type] || styles.message)}
          onClick={() => {
            if (t.convId) {
              const conv = useChatStore.getState().conversations.find((c) => c._id === t.convId);
              if (conv) useChatStore.getState().setActiveConversation(conv);
            }
            removeToast(t.id);
          }}
        >
          {/* Status reaction toast — redesigned */}
          {t.type === "statusReaction" ? (
            <div className={styles.reactionToast}>
              <div className={styles.statusThumb}>
                {t.statusPreview?.mediaType === "image" && t.statusPreview?.media ? (
                  <img src={t.statusPreview.media} alt="" className={styles.statusThumbImg} />
                ) : t.statusPreview?.mediaType === "video" && t.statusPreview?.media ? (
                  <video src={t.statusPreview.media} className={styles.statusThumbImg} muted />
                ) : (
                  <div
                    className={styles.statusThumbText}
                    style={{ background: t.statusPreview?.bgColor || "#7b7ff5" }}
                  >
                    <span>{(t.statusPreview?.textContent || "").slice(0, 10)}</span>
                  </div>
                )}
                <span className={styles.statusThumbEmoji}>{t.emoji}</span>
              </div>
              <div className={styles.reactionInfo}>
                <div className={styles.reactionTop}>
                  {t.avatar
                    ? <img src={t.avatar} alt={t.name} className={styles.reactionAvatar} />
                    : <span className={styles.reactionAvatarInitials}>{getInitials(t.name)}</span>
                  }
                  <span className={styles.reactionName}>{t.name}</span>
                </div>
                <span className={styles.reactionSub}>reacted {t.emoji} to your status</span>
              </div>
            </div>
          ) : (
            <div className={styles.avatarWrap}>
              {t.avatar
                ? <img src={t.avatar} alt={t.name} className={styles.avatarImg} />
                : <span className={styles.avatarInitials}>{getInitials(t.name)}</span>}
              {t.type === "online"  && <span className={styles.dot + " " + styles.dotGreen} />}
              {t.type === "offline" && <span className={styles.dot + " " + styles.dotGrey} />}
              {t.type === "message" && <span className={styles.dot + " " + styles.dotBlue} />}
            </div>
          )}

          {t.type !== "statusReaction" && (
            <div className={styles.body}>
              <span className={styles.toastName}>{t.name}</span>
              <span className={styles.toastSub}>
                {t.type === "online"  ? "is now online"
                 : t.type === "offline" ? "went offline"
                 : t.subtext}
              </span>
            </div>
          )}

          <button className={styles.closeBtn} onClick={(e) => { e.stopPropagation(); removeToast(t.id); }}>✕</button>
        </div>
      ))}
    </div>
  );
};

export default OnlineNotification;
